import { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { Alert, Badge, Button, Form, Modal, Spinner } from 'react-bootstrap';

import {
  GetSongpopMaestroFeatureQuery,
  UpdateSongpopMaestroConfigMutation,
  UpdateSongpopMaestroConfigMutationVariables,
} from '../../../../../../__gqltypes__';
import { SONGPOP_MAESTRO_FEATURE_QUERY, UPDATE_SONGPOP_MAESTRO_CONFIG } from '../graphql';

// availableForNonKids is the sales switch (named from when it covered non-kids only)
type SwitchField = 'enabled' | 'availableForKids' | 'availableForNonKids';

const CONFIRM_TEXTS: Record<SwitchField, Record<'on' | 'off', { title: string; body: string; button: string }>> = {
  enabled: {
    on: {
      title: 'Turn Songpop Maestro on?',
      body: 'Songpop Maestro shows in the app again: owners get their perks, Maestro leaderboards, Founders wall and badges back, and every running event starts selling to its audience. It can take a few minutes to show in the app.',
      button: 'Turn Songpop Maestro on',
    },
    off: {
      title: 'Turn Songpop Maestro off?',
      body: 'Songpop Maestro disappears for every player, owners included: no offer, no pushes, no perks, no Maestro leaderboards, Founders wall, badges or MAESTRO Gacha boxes. Nothing is deleted: it all comes back when turned on again.',
      button: 'Turn Songpop Maestro off',
    },
  },
  availableForKids: {
    on: {
      title: 'Turn Songpop Maestro on for kids mode?',
      body: 'Kids mode accounts get Songpop Maestro like every other player: the offer, the shop card and the pushes of the running events, and owners get their perks, owner status, Maestro leaderboards, Founders wall and badges. It can take a few minutes to show in the app.',
      button: 'Turn on for kids mode',
    },
    off: {
      title: 'Turn Songpop Maestro off for kids mode?',
      body: 'Kids mode accounts no longer see or get anything Songpop Maestro, owners included: no offer, no pushes, no perks, no owner status, no Maestro leaderboards, Founders wall or badges. Nothing is deleted: it all comes back when turned on again.',
      button: 'Turn off for kids mode',
    },
  },
  availableForNonKids: {
    on: {
      title: 'Resume the sales?',
      body: 'Every running event sells to its audience again: the offer, the shop card and the pushes are back (kids mode only while the Kids mode switch is on). It can take a few minutes to show in the app.',
      button: 'Resume the sales',
    },
    off: {
      title: 'Pause every sale?',
      body: 'Nobody is offered the pack any more, kids mode included: no offer, no shop card, no pushes. The events are not touched, and owners keep their perks, Maestro leaderboards, Founders wall and badges.',
      button: 'Pause the sales',
    },
  },
};

/**
 * Global switches of the whole feature (GameConfig features.songpopMaestro), checked before any
 * drop schedule: the master switch (`enabled`, sales open and close with the events), the kids
 * mode one (`availableForKids`, off by default) and the sales one (`availableForNonKids`, pauses
 * every sale without touching the events). Each one is saved on its own, so toggling it never
 * resends the perk values of the Settings tab.
 */
export function MaestroPackFeatureSwitch() {
  const { data, loading, refetch } = useQuery<GetSongpopMaestroFeatureQuery>(SONGPOP_MAESTRO_FEATURE_QUERY, {
    fetchPolicy: 'no-cache',
  });
  const [updateConfig, { loading: saving, error }] = useMutation<
    UpdateSongpopMaestroConfigMutation,
    UpdateSongpopMaestroConfigMutationVariables
  >(UPDATE_SONGPOP_MAESTRO_CONFIG, {
    onCompleted: () => refetch(),
    // the Founders wall section reads availableForKids too
    refetchQueries: ['getSongpopMaestroConfig'],
  });
  const [pending, setPending] = useState<{ field: SwitchField; value: boolean } | null>(null);

  const config = data?.liveOps.songpopMaestroConfig;
  const enabled = config?.enabled;
  const availableForKids = config?.availableForKids;
  const salesOn = config?.availableForNonKids;
  const texts = pending ? CONFIRM_TEXTS[pending.field][pending.value ? 'on' : 'off'] : null;

  const apply = () => {
    if (pending === null) {
      return;
    }
    updateConfig({ variables: { input: { [pending.field]: pending.value } } })
      .catch(() => {
        // surfaced through error
      })
      .finally(() => setPending(null));
  };

  return (
    <div className="mb-3">
      <div className="d-flex align-items-center">
        <h4 className="mb-0 mr-3">Songpop Maestro</h4>
        {loading || enabled === undefined ? (
          <Spinner animation="border" size="sm" />
        ) : (
          <>
            <Form.Check
              type="switch"
              id="maestro-feature-enabled"
              label=""
              checked={enabled}
              disabled={saving}
              onChange={(e) => setPending({ field: 'enabled', value: e.target.checked })}
            />
            <Badge variant={enabled ? 'success' : 'secondary'}>
              {enabled ? 'ON: visible in the app' : 'OFF: hidden from every player'}
            </Badge>
            {saving && <Spinner animation="border" size="sm" className="ml-2" />}
          </>
        )}
      </div>
      <small className="text-muted d-block">
        Master switch of the whole feature. <strong>ON</strong>: Songpop Maestro is live; each event sells on its own
        schedule. <strong>OFF</strong>: everything Songpop Maestro is hidden from every player, owners included: offer,
        shop card, pushes, perks, Maestro leaderboards and their rewards, Founders wall, badges and MAESTRO Gacha boxes.
        Nothing is lost: passes, scores and rewards come back when it is turned on again, and a payment in progress is
        still granted.
      </small>

      {availableForKids !== undefined && (
        <>
          <div className="d-flex align-items-center mt-2">
            <Form.Check
              type="switch"
              id="maestro-available-for-kids"
              label="Kids mode"
              checked={availableForKids}
              disabled={saving}
              onChange={(e) => setPending({ field: 'availableForKids', value: e.target.checked })}
            />
            <Badge variant={availableForKids ? 'success' : 'secondary'} className="ml-2">
              {availableForKids ? 'ON: kids mode accounts get Songpop Maestro' : 'OFF: hidden from kids mode accounts'}
            </Badge>
          </div>
          <small className="text-muted d-block">
            Songpop Maestro for kids mode accounts, under the master switch. <strong>OFF</strong> (the default): kids
            mode accounts see and get nothing Songpop Maestro, owners included: offer, shop card, pushes, perks, owner
            status, Maestro leaderboards and their rewards, Founders wall and badges. The Gacha shop is always closed to
            kids mode. Nothing is lost: it all comes back when turned on again, and a payment in progress is still
            granted.
          </small>
        </>
      )}

      {salesOn !== undefined && (
        <>
          <div className="d-flex align-items-center mt-2">
            <Form.Check
              type="switch"
              id="maestro-sales"
              label="Sales"
              checked={salesOn}
              disabled={saving}
              onChange={(e) => setPending({ field: 'availableForNonKids', value: e.target.checked })}
            />
            <Badge variant={salesOn ? 'success' : 'warning'} className="ml-2">
              {salesOn ? 'ON: the events sell on their schedule' : 'PAUSED: nobody is offered the pack'}
            </Badge>
          </div>
          <small className="text-muted d-block">
            Pause switch of every sale, under the master switch. <strong>OFF</strong>: nobody is offered the pack
            (offer, shop card, pushes), kids mode included, whatever the events say; the events are not touched. Owners
            keep everything. Use it to stop selling without ending an event (ending or drafting the only started event
            hides the Maestro leaderboards and the Founders wall again).
          </small>
        </>
      )}

      {error && (
        <Alert variant="danger" className="mt-2">
          {error.message}
        </Alert>
      )}

      <Modal show={pending !== null} onHide={() => setPending(null)}>
        <Modal.Header closeButton>
          <Modal.Title>{texts?.title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>{texts?.body}</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setPending(null)}>
            Cancel
          </Button>
          <Button variant={pending?.value ? 'success' : 'danger'} onClick={apply}>
            {texts?.button}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
